import {
  type NestedWorkspacePermissions,
  type WorkspaceRolePermissions,
} from "@/_store/roles.module.ts";
import { RolePermissionAccessMode, RolePermissionsScope } from "@/constants/roles.ts";
import { isWorkspaceFrozen } from "@/_helpers/subscription.ts";
import { GlobalPolicySection } from "@/constants/msp.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import isEmpty from "lodash/isEmpty";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import { toRaw } from "vue";
import get from "lodash/get";

// Make all properties non optional so that parameters
// to functions can be passed properly (`keyof` of selected scope) and autocomplete works
type RequiredWorkspaceRolePermissions = Required<WorkspaceRolePermissions>;

/**
 * Checks whether a specific action should be blocked for the current user.
 *
 * Combines two independent conditions — frozen workspace state and role-based
 * permission level. The action is blocked if either condition is true, so this
 * is the primary gate to use before allowing any mutating UI interaction
 * (e.g. enabling a toggle, opening an edit dialog).
 *
 * @param scopeName - Top-level permission scope (e.g. `RolePermissionsScope.PROTECTION`)
 * @param key - Permission key within the scope (e.g. `SubscriptionModule.ENDPOINT_SECURITY`)
 * @param nestedPermissionKey - Optional sub-key for granular action-level checks
 *   inside a `NestedWorkspacePermissions` object (e.g. a specific CRUD action flag)
 * @returns `true` when the action must be disabled, `false` when it is allowed
 *
 * @see isWorkspaceFrozen
 * @see isActionRestricted
 */
export function isWorkspaceFrozenOrActionRestricted<
  T extends RolePermissionsScope,
  K extends keyof RequiredWorkspaceRolePermissions[T],
  V extends (RequiredWorkspaceRolePermissions[T][K] extends NestedWorkspacePermissions
    ? NonNullable<RequiredWorkspaceRolePermissions[T][K]["editAccessModePermission"]>
    : undefined),
>(scopeName: T, key: NonNullable<K>, nestedPermissionKey?: keyof V) {
  return isWorkspaceFrozen() || isActionRestricted(scopeName, key, nestedPermissionKey);
}

/**
 * Checks whether a specific action is restricted by the current user's role permissions.
 *
 * Only evaluates the role-level access mode — does NOT account for workspace frozen state.
 * Prefer `isWorkspaceFrozenOrActionRestricted` for most UI guards; use this directly
 * only when you need to distinguish the two conditions separately.
 *
 * Resolution order:
 * 1. No permission entry found → restricted (returns `true`)
 * 2. Permission is a plain string → restricted unless it equals `EDIT`
 * 3. Permission is a `NestedWorkspacePermissions` object:
 *    - Without `nestedPermissionKey` → restricted unless `accessMode === EDIT`
 *    - With `nestedPermissionKey` → restricted unless the specific action flag is `true`
 *
 * @param scopeName - Top-level permission scope
 * @param key - Permission key within the scope
 * @param nestedPermissionKey - Optional sub-key for granular action checks
 * @returns `true` when the action is restricted, `false` when allowed
 */
export function isActionRestricted<
  T extends RolePermissionsScope,
  K extends keyof RequiredWorkspaceRolePermissions[T],
  V extends (RequiredWorkspaceRolePermissions[T][K] extends NestedWorkspacePermissions
    ? NonNullable<RequiredWorkspaceRolePermissions[T][K]["editAccessModePermission"]>
    : undefined),
>(scopeName: T, key: NonNullable<K>, nestedPermissionKey?: keyof V) {
  const permissions = getPermissionsByScopeNameAndKey(scopeName, key);
  // Filter case when permissions doesn't exist
  if (!permissions) return true;

  // Filter case when permissions is just string
  if (typeof permissions === "string") {
    return permissions !== RolePermissionAccessMode.EDIT;
  }

  // Filter case when permissions is an object of such shape
  // {
  //   accessMode: RolePermissionAccessMode;
  //   editAccessModePermission?: {[key: string]: boolean}
  // };

  if (typeof permissions === "object") {
    // If no key provided, then check if it's edit or not
    if (!nestedPermissionKey) {
      return permissions.accessMode !== RolePermissionAccessMode.EDIT;
    }

    // If key is provided, then check the particular action
    if (
      nestedPermissionKey &&
      permissions.accessMode === RolePermissionAccessMode.EDIT &&
      permissions.editAccessModePermission
    ) {
      // Indexing into a union of edit-permission shapes — TS can't narrow `keyof V` across the
      // union here. The structural cast to `Record<string, boolean>` matches what each variant
      // actually is at runtime.
      return permissions.editAccessModePermission
        ? !(permissions.editAccessModePermission as unknown as Record<string, boolean>)[
            nestedPermissionKey as string
          ]
        : true;
    }
  }

  return true;
}

/**
 * Checks whether the current user has NO_ACCESS for a given permission scope and key.
 *
 * Use this to conditionally hide entire UI sections (e.g. a whole settings tab or
 * navigation item) that the user should not see at all — not just disable interactions.
 * For disabling actions within a visible section, use `isActionRestricted` instead.
 *
 * @param scopeName - Top-level permission scope
 * @param key - Permission key within the scope
 * @returns `true` when the user has no access (section must be hidden), `false` when
 *   the user has at least VIEW or EDIT access
 */
export function isAccessRestricted<
  T extends RolePermissionsScope = RolePermissionsScope,
  K extends keyof RequiredWorkspaceRolePermissions[T] =
    keyof WorkspaceRolePermissions[RolePermissionsScope],
>(scopeName: T, key: K) {
  const permissions = getPermissionsByScopeNameAndKey(scopeName, key);
  if (!permissions) return true;

  if (typeof permissions === "string") {
    return permissions === RolePermissionAccessMode.NO_ACCESS;
  }
  return permissions.accessMode === RolePermissionAccessMode.NO_ACCESS;
}

/**
 * Reads the raw permission value for a given scope and key from the workspace permissions store.
 *
 * Returns the permission entry exactly as stored — either a plain `RolePermissionAccessMode`
 * string or a `NestedWorkspacePermissions` object — without interpreting it. Callers
 * (`isActionRestricted`, `isAccessRestricted`) are responsible for applying the resolution
 * logic on top of this value.
 *
 * The store value is unwrapped with `toRaw()` before the `lodash/get` traversal so that
 * reactive Vue proxies don't interfere with property access on deeply nested objects.
 *
 * @param scopeName - Top-level permission scope (e.g. `RolePermissionsScope.PROTECTION`)
 * @param key - Permission key within the scope (e.g. a `SubscriptionModule` member)
 * @returns The raw permission value, or `undefined` when the scope/key pair is absent from
 *   the current workspace permissions
 */
export function getPermissionsByScopeNameAndKey<
  T extends RolePermissionsScope = RolePermissionsScope,
  K extends keyof RequiredWorkspaceRolePermissions[T] =
    keyof WorkspaceRolePermissions[RolePermissionsScope],
>(scopeName: T, key: K) {
  const permissionsStore = usePermissionsStore();
  const currentWorkspacePermissions = toRaw(permissionsStore.currentWorkspacePermissions);
  return get(currentWorkspacePermissions, [scopeName, key]) as
    NestedWorkspacePermissions | RolePermissionAccessMode | undefined;
}

/**
 * Checks whether the current workspace is allowed to override the MSP global policy
 * for a specific settings section.
 *
 * MSP administrators can lock individual settings sections via a global policy
 * (`mspPolicyShortInfo`). A workspace can edit a locked section only when the MSP has
 * explicitly granted override rights (`allowPolicyOverriding === true`). If the section
 * is not covered by the global policy at all, it is always editable regardless of the
 * override flag.
 *
 * Decision table:
 * | `mspPolicyShortInfo` present | section in policy | `allowPolicyOverriding` | result |
 * |------------------------------|-------------------|-------------------------|--------|
 * | No (empty)                   | —                 | —                       | `true` |
 * | Yes                          | No                | —                       | `true` |
 * | Yes                          | Yes               | `true`                  | `true` |
 * | Yes                          | Yes               | `false`                 | `false` |
 *
 * @param currentSection - The settings section being evaluated (e.g. `GlobalPolicySection.ENDPOINT`)
 * @returns `true` when the workspace may freely edit this section, `false` when the MSP
 *   policy locks it and overriding is not permitted
 */
export const canOverrideGlobalPolicy = (currentSection: GlobalPolicySection) => {
  const { workspace } = useWorkspaceStore();
  // if no global settings info then everything is editable
  if (isEmpty(workspace.mspPolicyShortInfo)) return true;
  const isSectionPresentInGlobalPolicy =
    workspace.mspPolicyShortInfo.sections.includes(currentSection);
  return workspace.allowPolicyOverriding || !isSectionPresentInGlobalPolicy;
};
